import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FiPlus, FiMail, FiEdit, FiTrash2, FiFileText, FiLock, FiSearch, FiFilter, FiSend, FiClock } from 'react-icons/fi';
import { API_URL } from '../config';
import { useAuth } from '../contexts/AuthContext';

function Letters() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.can_manage_payroll;

  const [letters, setLetters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const loadLetters = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (debouncedSearch) params.append('search', debouncedSearch);
      const res = await axios.get(`${API_URL}/letters?${params}`, config);
      setLetters(res.data || []);
    } catch (err) {
      console.error('Failed to load letters:', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, debouncedSearch]);

  useEffect(() => {
    loadLetters();
  }, [loadLetters]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this letter? This cannot be undone.')) return;
    try {
      const token = localStorage.getItem('authToken');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${API_URL}/letters/${id}`, config);
      loadLetters();
    } catch (err) {
      alert('Failed to delete letter: ' + (err.response?.data?.error || err.message));
    }
  };

  const statusBadge = (status) => {
    const styles = {
      draft: { bg: '#fef3c7', color: '#92400e', label: 'Draft' },
      sent: { bg: '#dcfce7', color: '#166534', label: 'Sent' },
    };
    const s = styles[status] || styles.draft;
    return (
      <span style={{
        display: 'inline-block', padding: '3px 10px', backgroundColor: s.bg, color: s.color,
        borderRadius: '12px', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase',
      }}>
        {s.label}
      </span>
    );
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ margin: '0 0 4px', fontSize: '24px', color: '#111827', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <FiMail /> Letters
          </h1>
          <p style={{ margin: 0, color: '#6b7280', fontSize: '14px' }}>
            Compose and send letterhead correspondence.
          </p>
        </div>
        <button
          onClick={() => navigate('/letters/new')}
          style={{
            padding: '10px 18px', backgroundColor: '#2563eb', color: 'white',
            border: 'none', borderRadius: '8px', cursor: 'pointer',
            fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px',
          }}
        >
          <FiPlus /> New Letter
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1 1 240px', minWidth: '200px' }}>
          <FiSearch style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
          <input
            type="text"
            placeholder="Search subject, recipient..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: '100%', padding: '9px 12px 9px 36px', border: '1px solid #d1d5db',
              borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box',
            }}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#6b7280', fontSize: '13px' }}>
          <FiFilter size={14} /> Status:
        </div>
        {['all', 'draft', 'sent'].map(opt => (
          <button
            key={opt}
            onClick={() => setStatusFilter(opt)}
            style={{
              padding: '8px 14px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px',
              fontWeight: 600, border: `1.5px solid ${statusFilter === opt ? '#2563eb' : '#e5e7eb'}`,
              backgroundColor: statusFilter === opt ? '#eff6ff' : 'white',
              color: statusFilter === opt ? '#2563eb' : '#6b7280',
              textTransform: 'capitalize',
            }}
          >
            {opt}
          </button>
        ))}
      </div>

      {/* Letters list */}
      <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>Loading...</div>
        ) : letters.length === 0 ? (
          <div style={{ padding: '60px 20px', textAlign: 'center', color: '#9ca3af' }}>
            <FiFileText size={36} style={{ marginBottom: '12px', opacity: 0.4 }} />
            <div style={{ fontSize: '14px' }}>No letters yet. Click "New Letter" to compose one.</div>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '11px', textTransform: 'uppercase', color: '#6b7280', fontWeight: 600 }}>Subject</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '11px', textTransform: 'uppercase', color: '#6b7280', fontWeight: 600 }}>Recipient</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '11px', textTransform: 'uppercase', color: '#6b7280', fontWeight: 600 }}>Reference</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '11px', textTransform: 'uppercase', color: '#6b7280', fontWeight: 600 }}>Status</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '11px', textTransform: 'uppercase', color: '#6b7280', fontWeight: 600 }}>Updated</th>
                <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '11px', textTransform: 'uppercase', color: '#6b7280', fontWeight: 600 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {letters.map(letter => (
                <tr
                  key={letter.id}
                  onClick={() => navigate(`/letters/${letter.id}`)}
                  style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                >
                  <td style={{ padding: '14px 16px', fontSize: '14px', color: '#111827', fontWeight: 500 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {letter.visibility === 'admin_only' && (
                        <FiLock size={12} style={{ color: '#dc2626' }} title="Admin only" />
                      )}
                      {letter.subject || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>(no subject)</span>}
                    </div>
                  </td>
                  <td style={{ padding: '14px 16px', fontSize: '13px', color: '#374151' }}>
                    {letter.recipient_name || letter.recipient_company || letter.recipient_email || <span style={{ color: '#9ca3af' }}>—</span>}
                  </td>
                  <td style={{ padding: '14px 16px', fontSize: '13px', color: '#374151' }}>
                    {letter.related_label ? (
                      <span style={{ padding: '2px 8px', backgroundColor: '#eff6ff', color: '#2563eb', borderRadius: '4px', fontSize: '12px' }}>
                        {letter.related_label}
                      </span>
                    ) : (
                      <span style={{ color: '#9ca3af' }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: '14px 16px' }}>{statusBadge(letter.status)}</td>
                  <td style={{ padding: '14px 16px', fontSize: '12px', color: '#6b7280' }}>
                    {letter.sent_at ? (
                      <span title={`Sent ${new Date(letter.sent_at + 'Z').toLocaleString()}`}>
                        <FiSend size={11} style={{ marginRight: '4px' }} />
                        {new Date(letter.sent_at + 'Z').toLocaleDateString()}
                      </span>
                    ) : (
                      <span title={`Updated ${new Date(letter.updated_at + 'Z').toLocaleString()}`}>
                        <FiClock size={11} style={{ marginRight: '4px' }} />
                        {new Date(letter.updated_at + 'Z').toLocaleDateString()}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '14px 16px', textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => navigate(`/letters/${letter.id}`)}
                      style={{
                        padding: '6px 10px', marginRight: '6px', backgroundColor: '#eff6ff',
                        color: '#2563eb', border: '1px solid #bfdbfe', borderRadius: '6px',
                        cursor: 'pointer', fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '4px',
                      }}
                    >
                      <FiEdit size={12} /> Open
                    </button>
                    {(letter.created_by_id === user?.id || isAdmin) && (
                      <button
                        onClick={() => handleDelete(letter.id)}
                        style={{
                          padding: '6px 10px', backgroundColor: 'white', color: '#dc2626',
                          border: '1px solid #fecaca', borderRadius: '6px', cursor: 'pointer',
                          fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '4px',
                        }}
                      >
                        <FiTrash2 size={12} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default Letters;
